import { formatDate, formatTZS } from "../../../utils/format";

export const COLLATERAL_TABS = [
  { id: "all", label: "All Items" },
  { id: "pending_valuation", label: "Pending Valuation" },
  { id: "active", label: "Active" },
  { id: "issue", label: "Issues" },
  { id: "released", label: "Released" },
];

export const COLLATERAL_STATUS_META = {
  pending_valuation: { label: "Pending", tone: "warning" },
  active: { label: "Active", tone: "success" },
  issue: { label: "Issue", tone: "danger" },
  released: { label: "Released", tone: "secondary" },
  liquidated: { label: "Liquidated", tone: "neutral" },
};

export const COLLATERAL_TYPE_META = {
  vehicle: { label: "Vehicle", tone: "info" },
  equipment: { label: "Equipment", tone: "primary" },
  real_estate: { label: "Real Estate", tone: "success" },
  inventory: { label: "Inventory", tone: "warning" },
  receivables: { label: "Receivables", tone: "info" },
  cash_deposit: { label: "Cash Deposit", tone: "success" },
  other: { label: "Other", tone: "secondary" },
};

export const COLLATERAL_TYPE_OPTIONS = Object.entries(COLLATERAL_TYPE_META).map(([value, meta]) => ({
  value,
  label: meta.label,
}));

export const COLLATERAL_ISSUE_OPTIONS = [
  { value: "missing_documentation", label: "Missing documentation" },
  { value: "expired_valuation", label: "Expired valuation" },
  { value: "ltv_breach", label: "LTV breach" },
  { value: "verification_failed", label: "Verification failed" },
  { value: "other", label: "Other" },
];

export const COLLATERAL_ISSUE_META = COLLATERAL_ISSUE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = { label: option.label, tone: "danger" };
  return acc;
}, { none: { label: "None", tone: "secondary" } });

export function filterCollateralRows(records = [], { tabFilter = "all", search = "" } = {}) {
  const q = search.trim().toLowerCase();
  return records.filter((row) => {
    if (tabFilter !== "all" && row.status !== tabFilter) {
      return false;
    }
    if (!q) {
      return true;
    }
    const haystack = [
      row.description,
      row.identifier,
      row.borrower_name,
      row.borrower_ref,
      row.loan_reference,
      row.location,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function computeCollateralSummary(records = [], summary = {}) {
  return {
    total: summary.total_count ?? records.length,
    active: summary.active_count ?? records.filter((row) => row.status === "active").length,
    pending: summary.pending_valuation_count ?? records.filter((row) => row.status === "pending_valuation").length,
    issues: summary.issue_count ?? records.filter((row) => row.status === "issue").length,
    totalValue: Number(summary.total_value ?? records.reduce((sum, row) => sum + Number(row.estimated_value || 0), 0)),
  };
}

export function exportCollateralCsv(rows) {
  const header = [
    "Borrower Ref",
    "Borrower",
    "Type",
    "Description",
    "Identifier",
    "Value",
    "Status",
    "Issue",
    "Loan Ref",
    "Updated",
  ];
  const lines = rows.map((row) =>
    [
      row.borrower_ref,
      row.borrower_name,
      COLLATERAL_TYPE_META[row.collateral_type]?.label || row.collateral_type,
      row.description,
      row.identifier || "",
      formatTZS(row.estimated_value),
      COLLATERAL_STATUS_META[row.status]?.label || row.status,
      COLLATERAL_ISSUE_META[row.issue_reason]?.label || row.issue_reason,
      row.loan_reference || "",
      formatDate(row.updated_at),
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(",")
  );
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `collateral-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
